/**
 * Firebase Analytics
 * 
 * Provides analytics functionality using Firebase Analytics with privacy compliance
 * and performance optimization. Only tracks when user has given analytics consent.
 */

import type { Analytics } from 'firebase/analytics';

import { initializeFirebaseAnalytics } from '../utils/firebaseLoader';
import { logger } from '../utils/logger';

// Cache the analytics instance to avoid repeated initialization
let analyticsInstance: Analytics | null = null;
let initializationPromise: Promise<Analytics | null> | null = null;

/**
 * Get or initialize Firebase Analytics instance
 */
const getAnalyticsInstance = async (): Promise<Analytics | null> => {
  // Return cached instance if available
  if (analyticsInstance) {
    return analyticsInstance;
  }

  // Return existing promise if initialization is in progress
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start new initialization
  initializationPromise = initializeFirebaseAnalytics();
  analyticsInstance = await initializationPromise;
  initializationPromise = null;

  return analyticsInstance;
};

/**
 * Log a page view event to Firebase Analytics
 * @param pathname - The current page path
 * @param title - Optional page title
 */
export const logPageView = async (pathname: string, title?: string): Promise<void> => {
  try {
    // Get analytics instance
    const analytics = await getAnalyticsInstance();
    
    if (!analytics) {
      // In development, log to console even without analytics
      if (process.env.NODE_ENV === 'development') {
        logger.info(`Page view: ${pathname}${title ? ` (${title})` : ''}`);
      }
      return;
    }

    // Dynamically import logEvent to avoid loading Firebase Analytics bundle upfront
    const { logEvent } = await import('firebase/analytics');
    
    // Log page view event
    logEvent(analytics, 'page_view', {
      page_path: pathname,
      page_title: title || document.title,
      page_location: window.location.href,
    });

    logger.info(`Firebase Analytics: Page view logged for ${pathname}`);
  } catch (error) {
    logger.error('Failed to log page view to Firebase Analytics', { 
      pathname, 
      title, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    });
  }
};
